/* eslint-disable no-console */
// #region IMPORTS
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { informUser, TOAST_TYPES } from '@azmed-org/azcomponents';
import i18n from '@ohif/i18n';
import {
  bulkDeleteResources,
  fetchPatientsResponse,
  postInstance,
  putSeriesAttachment,
} from '@ohif/core/src/utils/orthancAPI';

import { REPORT_ATTACHMENT_ID } from '../../Data/reportSeries';
import { selectStudies } from '../../StudiesSlice';
import { createTaskQueue } from '../taskQueue';
import { extractDicom, listDicoms } from '../zipReader';
// #endregion

// #region CONSTANTS
const TOAST_ID = 'offline-import';
const STUDY_LIST_URL = '/';
const UPLOAD_CONCURRENCY = 8;
const REPORT_CONCURRENCY = 4;
// #endregion

// #region UTILS
// Translate a key of the StudyList namespace (the toast is outside providers)
const translateStudyList = (key, options) =>
  i18n.t(`StudyList:${key}`, options);

// Reload onto the study list, which reads the new local data
const reloadStudyList = () => window.location.assign(STUDY_LIST_URL);

// Show the progress toast with the number of DICOM instances sent so far, out
// of the total once the archive is listed
const showProgress = run => {
  // The Cancel action aborts the run and keeps the toast open
  const cancel = {
    label: i18n.t('Buttons:Cancel'),
    onClick: event => {
      event.preventDefault();
      run.controller.abort();
    },
  };

  // Title with the total only once it is known
  const title =
    run.total === null
      ? translateStudyList('Import… {{sent}} DICOM sent', { sent: run.sent })
      : translateStudyList('Import… {{sent}} / {{total}} DICOM sent', {
          sent: run.sent,
          total: run.total,
        });

  informUser({
    type: TOAST_TYPES.info,
    title,
    toastId: TOAST_ID,
    props: { duration: Infinity, dismissible: false, action: cancel },
  });
};

// Erase the local data through Orthanc. Not abortable on purpose: Orthanc
// finishes the deletion anyway
const resetLocalData = async () => {
  // Ids of every patient stored in the local Orthanc
  const patientsResponse = await fetchPatientsResponse();
  if (!patientsResponse.ok) {
    throw new Error(`Reset failed (${patientsResponse.status})`);
  }

  const ids = await patientsResponse.json();
  // Nothing to delete
  if (!ids.length) {
    return;
  }

  // Delete every patient at once; Orthanc answers once the deletion is done
  const deleteResponse = await bulkDeleteResources(ids);
  if (!deleteResponse.ok) {
    throw new Error(`Reset failed (${deleteResponse.status})`);
  }
};

// Files to import from the picked folder, skipping macOS hidden files; fails
// when it holds no archive
const selectFiles = files => {
  const visible = files.filter(file => !file.name.startsWith('.'));
  const archive = visible.find(file => file.name.endsWith('.zip'));
  const reportsFile = visible.find(file => file.name.endsWith('.json'));

  if (!archive) {
    throw new Error(translateStudyList('No archive found in this folder.'));
  }

  return { archive, reportsFile };
};

// Extract and POST one DICOM; never rejects, a failed DICOM is logged,
// counted on the run and skipped
const uploadDicom = async (run, archive, dicom) => {
  const { signal } = run.controller;

  if (signal.aborted) {
    return;
  }

  try {
    // Send the extracted DICOM to Orthanc
    const body = await extractDicom(archive, dicom, signal);
    const { status } = await postInstance(body, signal);

    if (status < 200 || status >= 300) {
      throw new Error(`Upload failed (${status})`);
    }

    // Count the sent instance in the progress
    run.sent += 1;
    showProgress(run);
  } catch (error) {
    // An abort is handled by the run itself
    if (error.name === 'AbortError') {
      return;
    }

    // Log and count the skipped DICOM
    console.warn('[offlineImport] DICOM skipped:', dicom.filename, error);
    run.failed += 1;
  }
};

// Send the DICOM files of one archive to Orthanc, a few at once
const uploadArchive = async (run, archive, dicoms) => {
  const { signal } = run.controller;
  const queue = createTaskQueue(UPLOAD_CONCURRENCY);

  for (const dicom of dicoms) {
    if (signal.aborted) {
      break;
    }

    await queue.schedule(() => uploadDicom(run, archive, dicom));
  }

  await queue.drain();

  // Fail on a cancel
  signal.throwIfAborted();
};

// Attach the report of a report line on its series; never rejects, a line not
// attached is only logged
const attachReport = async (reportLine, index, signal) => {
  // PUT the report, a malformed line throws inside the guard
  try {
    const { seriesOrthancId, report } = reportLine;
    const response = await putSeriesAttachment(
      seriesOrthancId,
      REPORT_ATTACHMENT_ID,
      report,
      signal
    );

    if (response.ok) {
      return;
    }
  } catch {
    // Falls through to the log
  }

  // Log the line not attached
  console.warn('[offlineImport] report not attached: line', index);
};

// Read a reports file then attach its report lines with a bounded concurrency;
// an invalid file or a line not attached is only logged
const processReportsFile = async (run, reportsFile) => {
  // Read the whole file; an invalid or unreadable one gives no report
  let reports;

  try {
    reports = JSON.parse(await reportsFile.text());

    if (!Array.isArray(reports)) {
      throw new Error('Reports file is not an array');
    }
  } catch (error) {
    console.warn(
      '[offlineImport] invalid reports file:',
      reportsFile.name,
      error
    );

    return;
  }

  // Abort signal of the run and the queue bounding the attachments
  const { signal } = run.controller;
  const queue = createTaskQueue(REPORT_CONCURRENCY);

  // Queue each report line, waiting for a free slot before the next one
  try {
    for (const [index, reportLine] of reports.entries()) {
      signal.throwIfAborted();
      await queue.schedule(() => attachReport(reportLine, index, signal));
    }
  } finally {
    await queue.drain();
  }
};

// Show the end toast, kept until Reload, its only way out
const showEndToast = (type, title, message) => {
  informUser({
    type,
    title,
    message,
    toastId: TOAST_ID,
    props: {
      duration: Infinity,
      dismissible: false,
      action: { label: translateStudyList('Reload'), onClick: reloadStudyList },
    },
  });
};

// End of an import process: reload on a full success or a cancel, else show
// the skipped DICOM or why it failed
const endImportProcess = (run, error) => {
  if (run.controller.signal.aborted || (!error && !run.failed)) {
    return reloadStudyList();
  }

  // Finished, but some DICOM were skipped
  if (!error) {
    return showEndToast(
      TOAST_TYPES.warning,
      translateStudyList('Import finished, {{failed}} DICOM skipped.', {
        failed: run.failed,
      })
    );
  }

  // Stopped by an error
  console.error('[offlineImport] import failed', error);
  showEndToast(
    TOAST_TYPES.error,
    translateStudyList('Import failed.'),
    error.message
  );
};

/**
 * Import the picked folder into the local Orthanc: its ZIP archive (the
 * current data being erased first) then its reports file. Reloads the study
 * list on success or cancel; the toast shows the progress, the Cancel action,
 * the skipped DICOM and the failure.
 * @param {File[]} files Every file of the picked folder
 * @returns {Promise<void>} Settles once the run is over
 */
const runOfflineImport = async files => {
  // Mutable state of the run: DICOM instances sent, skipped and to send
  const run = {
    controller: new AbortController(),
    sent: 0,
    failed: 0,
    total: null,
  };

  // Show the progress while the steps run
  showProgress(run);

  // Every step of the import, in order, with cancel checkpoints between them
  const { signal } = run.controller;
  let error = null;

  try {
    // Read everything before anything is erased
    const { archive, reportsFile } = selectFiles(files);
    const dicoms = await listDicoms(archive);
    run.total = dicoms.length;

    // Last cancel point before the local data changes
    signal.throwIfAborted();
    await resetLocalData();

    // Upload the archive, then attach the reports
    await uploadArchive(run, archive, dicoms);

    if (reportsFile) {
      await processReportsFile(run, reportsFile);
    }

    // Last cancel point after the reports
    signal.throwIfAborted();
  } catch (caught) {
    // Keep what stopped the run
    error = caught;
  } finally {
    endImportProcess(run, error);
  }
};
// #endregion

// #region HOOK
/**
 * Logic of the offline import: picked files handling, native replace
 * confirmation when local data exists, then the import run.
 * @param {function} [onImportStart] Called once the import is confirmed
 * @returns {{t: function, handleFilesSelected: function}} The view
 */
export const useOfflineImport = onImportStart => {
  // #region INITIALIZATION
  const [t] = useTranslation(['StudyList', 'Buttons']);
  // Studies of the list, telling whether local data exists
  const { studies } = useSelector(selectStudies);
  // #endregion

  // #region EVENT
  // Take the picked files, then clear the input so the same folder works again
  const handleFilesSelected = event => {
    const files = Array.from(event.target.files);
    event.target.value = '';

    // Nothing to import when the picked folder is empty
    if (!files.length) {
      return;
    }

    // Ask before replacing existing local data
    if (studies.length) {
      const message = `${t('Replace the data?')}\n\n${t(
        'The current data will be deleted.'
      )}`;

      // The user refused: keep the local data
      if (!window.confirm(message)) {
        return;
      }
    }

    // Notify the caller, then start the import
    onImportStart?.();
    runOfflineImport(files);
  };
  // #endregion

  // #region INTERFACE
  return {
    t,
    handleFilesSelected,
  };
  // #endregion
};
// #endregion
